import * as React from "react"
import "./styles/index.css"

import type { Content, Editor } from "@tiptap/react"
import type { UseMinimalTiptapEditorProps } from "./hooks/use-minimal-tiptap"
import { EditorContent } from "@tiptap/react"
import { Separator } from "@/components/ui/separator"
import {
  frameFooterVariants,
  framePanelVariants,
  frameVariants,
  useFrameVariant,
} from "@/components/ui/frame"
import { cn } from "@/lib/utils"
import { SectionOne } from "./components/section/one"
import { SectionTwo } from "./components/section/two"
import { SectionThree } from "./components/section/three"
import { SectionFour } from "./components/section/four"
import { SectionFive } from "./components/section/five"
import { LinkBubbleMenu } from "./components/bubble-menu/link-bubble-menu"
import { useMinimalTiptapEditor } from "./hooks/use-minimal-tiptap"
import { MeasuredContainer } from "./components/measured-container"

export interface MinimalTiptapProps
  extends Omit<UseMinimalTiptapEditorProps, "onUpdate"> {
  value?: Content
  onChange?: (value: Content) => void
  className?: string
  editorContentClassName?: string
  showToolBar?: boolean
  /**
   * "frame" makes the editor itself look like ui/frame.tsx without using it:
   * root = Frame, toolbar = FrameHeader, content = FramePanel, `footer` =
   * FrameFooter. Same class recipes, so it sits next to real Frames.
   */
  variant?: "default" | "frame"
  /** Rendered under the content, styled like a FrameFooter (frame variant only). */
  footer?: React.ReactNode
}

// The "frame" look uses ui/frame.tsx's own recipes, so it follows the
// enclosing Frame's variant (or the app-wide default) like a real Frame. The
// toolbar is its header row, and the content pads itself.
const FRAME_TOOLBAR = "px-2.5 py-2"
const FRAME_PANEL = "p-0 focus-within:border-primary"

const Toolbar = ({ editor, className }: { editor: Editor; className?: string }) => (
  <div className={cn("shrink-0 overflow-x-auto p-1.5 bg-muted rounded-t-[calc(var(--radius-md)-1px)]", className)}>
    <div className="flex w-max items-center gap-0.5">
      <SectionOne editor={editor} activeLevels={[1, 2, 3, 4, 5, 6]} />

      <Separator orientation="vertical" className="mx-1.5 h-5" />

      <SectionTwo
        editor={editor}
        activeActions={[
          "bold",
          "italic",
          "underline",
          "strikethrough",
          "code",
          "clearFormatting",
        ]}
        mainActionCount={3}
      />

      <Separator orientation="vertical" className="mx-1.5 h-5" />

      <SectionThree editor={editor} />

      <Separator orientation="vertical" className="mx-1.5 h-5" />

      <SectionFour
        editor={editor}
        activeActions={["orderedList", "bulletList"]}
        mainActionCount={0}
      />

      <Separator orientation="vertical" className="mx-1.5 h-5" />

      <SectionFive
        editor={editor}
        activeActions={["codeBlock", "blockquote", "horizontalRule"]}
        mainActionCount={0}
      />
    </div>
  </div>
)

export const MinimalTiptapEditor = React.forwardRef<
  HTMLDivElement,
  MinimalTiptapProps
>(
  (
    {
      value,
      onChange,
      className,
      editorContentClassName,
      showToolBar = true,
      variant = "default",
      footer,
      ...props
    },
    ref
  ) => {
    const editor = useMinimalTiptapEditor({
      value,
      onUpdate: onChange,
      ...props,
    })
    const frameVariant = useFrameVariant()

    if (!editor) {
      return null
    }

    const framed = variant === "frame"

    return (
      <MeasuredContainer
        as="div"
        name="editor"
        ref={ref}
        className={cn(
          "flex w-full flex-col h-auto",
          framed
            ? frameVariants({ variant: frameVariant })
            : "rounded-md border border-input bg-muted shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/10",
          className
        )}
      >
        {showToolBar && (
          <Toolbar
            editor={editor}
            className={framed ? cn("bg-transparent rounded-none", FRAME_TOOLBAR) : undefined}
          />
        )}

        <EditorContent
          editor={editor}
          className={cn(
            "minimal-tiptap-editor h-auto",
            framed ? cn(framePanelVariants({ variant: frameVariant }), FRAME_PANEL) : "bg-background rounded-[calc(var(--radius-md)-1px)]",
            editorContentClassName
          )}
        />
        {framed && footer && <div className={frameFooterVariants({ variant: frameVariant })}>{footer}</div>}
        <LinkBubbleMenu editor={editor} />
      </MeasuredContainer>
    )
  }
)

MinimalTiptapEditor.displayName = "MinimalTiptapEditor"

export default MinimalTiptapEditor
